import React, { useEffect, useRef, useState } from 'react';
import SharedCardContent from '@shared/SharedCardContent/SharedCardContent';
import TextField from '@shared/TextField/TextField';
import Button from '@shared/Button/Button';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { DocumentPage, useFilmTyping } from '@shared/FilmMotion/FilmMotion';
import './EmailWorkFlow.css';
import mailIcon from '@icons/email-workflow-icon.svg';
import mailLogo from '@icons/email-workflow-logo.png';
import { emailWorkflowData } from './emailWorkFlowData';
const {
    EMAIL,
    SUBJECT,
    BODY,
    DOC_TITLE,
    DOC_FILE,
    DOC_TEXT,
} = emailWorkflowData;

function useOnScreen(ref, rootMargin = '0px') {
    const [isIntersecting, setIntersecting] = useState(false);
    useEffect(() => {
        const observer = new window.IntersectionObserver(
            ([entry]) => setIntersecting(entry.isIntersecting),
            { rootMargin }
        );
        const currentNode = ref.current;
        if (currentNode) observer.observe(currentNode);
        return () => currentNode && observer.unobserve(currentNode);
    }, [ref, rootMargin]);
    return isIntersecting;
}

const typingDelay = 750;
const subjectDelay = 1025;
const bodyDelay = 2125;
const resetDelay = 5900;

function useTypeAnimation(target, enabled, delay) {
    const value = useFilmTyping(target, enabled, delay);
    const [showCursor, setShowCursor] = useState(true);
    useEffect(() => {
        if (!enabled) return undefined;
        const cursorInt = setInterval(() => setShowCursor(v => !v), 440);
        return () => clearInterval(cursorInt);
    }, [enabled]);
    return enabled ? value + (showCursor ? '|' : ' ') : value;
}

// The draft is set down as a whole page, the way the motion films present documents (no scrolling).
export function EmailWorkflowDocumentPreview({ text = DOC_TEXT, className = "", desk = false }) {
    return (
        <motion.div
            key="preview"
            className={`document-overlay document-overlay--page ${className}`.trim()}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
        >
            <DocumentPage text={text} desk={desk} />
        </motion.div>
    );
}

// The hero reuses the same attachment card and Word-style scrolling preview
// as the email product animation, while letting its storyboard control timing.
export function EmailWorkflowAttachmentPreview({ documentTitle = DOC_TITLE, fileName = DOC_FILE, text = DOC_TEXT, className = "", openDelay = 1500 }) {
    const [isOpen, setIsOpen] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setIsOpen(true), openDelay);
        return () => clearTimeout(timer);
    }, [openDelay]);

    return (
        <div className={`email-workflow-right email-workflow-right--hero-preview ${className}`.trim()}>
            <AnimatePresence mode="wait">
                {isOpen ? (
                    <EmailWorkflowDocumentPreview key="document" text={text} className="hero-document-preview" desk />
                ) : (
                    <motion.div key="attachment" className="email-workflow-draft-card hero-document-attachment"
                        initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: -8 }} transition={{ duration: 0.35 }}>
                        <div className="draft-card-logo"><img src={mailLogo} alt="" /></div>
                        <div className="draft-card-title">{documentTitle}</div>
                        <div className="draft-card-doc-link"><span className="draft-card-attach-icon" /><span className="draft-card-doc-fw">{fileName}</span></div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function EmailWorkFlow() {
    const containerRef = useRef(null);
    const inView = useOnScreen(containerRef, '-160px');
    const [step, setStep] = useState(0);
    const [field, setField] = useState({ email: '', subject: '', body: '' });
    const [showDoc, setShowDoc] = useState(false);

    // new cursor states
    const [cursorPos, setCursorPos] = useState({ x: 250, y: 150 });
    const [showCursor] = useState(true);
    const [clicking, setClicking] = useState(false);
    const [draftClicked, setDraftClicked] = useState(false);

    const typedEmail = useTypeAnimation(EMAIL, step === 1, typingDelay);
    const typedSubject = useTypeAnimation(SUBJECT, step === 2, subjectDelay);
    const typedBody = useTypeAnimation(BODY, step === 3, bodyDelay);

    useEffect(() => {
        if (!inView) return;
        let timer;

        if (step === 0) {
            setField({ email: '', subject: '', body: '' });
            setShowDoc(false);
            timer = setTimeout(() => setStep(1), 700);
        }
        else if (step === 1) {
            setField(f => ({ ...f, email: '' }));
            timer = setTimeout(() => setField(f => ({ ...f, email: EMAIL })), typingDelay);
            timer = setTimeout(() => setStep(2), typingDelay);
        }
        else if (step === 2) {
            setField(f => ({ ...f, subject: '' }));
            timer = setTimeout(() => setField(f => ({ ...f, subject: SUBJECT })), subjectDelay);
            timer = setTimeout(() => setStep(3), subjectDelay);
        }
        else if (step === 3) {
            setField(f => ({ ...f, body: '' }));
            timer = setTimeout(() => setField(f => ({ ...f, body: BODY })), bodyDelay);
            timer = setTimeout(() => setStep(4), bodyDelay + 400);
        }
        else if (step === 4) {
            setTimeout(() => {
                setCursorPos({ x: 310, y: 420 });
                setTimeout(() => {
                    setClicking(true);
                    setTimeout(() => setClicking(false), 250);
                    setTimeout(() => setStep(5), 700);
                }, 700);
            }, 400);
        }
        else if (step === 5) {
            const clickTimer = setTimeout(() => {
                setDraftClicked(true);
                setTimeout(() => setShowDoc(true), 2000);
            }, 2200);

            timer = setTimeout(() => {
                setStep(0);
                setDraftClicked(false);
            }, resetDelay + 3000);

            return () => {
                clearTimeout(timer);
                clearTimeout(clickTimer);
            };
        }



        return () => clearTimeout(timer);
    }, [step, inView]);

    useEffect(() => { if (!inView) setStep(0); }, [inView]);
    useEffect(() => { if (step === 0) setShowDoc(false); }, [step]);

    const isSending = step === 4;
    const sent = step === 5;
    return (
        <div className="email-workflow-container" ref={containerRef}>
            <div className="email-workflow-left">
                <SharedCardContent
                    icon={mailIcon}
                    badgeTitle="Email Workflow"
                    title="Work Where You Already Are"
                    text="Your agent manages the inbox with you. Collaborate entirely through email, with no extra dashboards, logins, or friction."
                    subtext="Outlook, Gmail, or any other email client. Vienna works directly in your inbox for natural, hands-off collaboration."
                />
            </div>

            <div className={"email-workflow-right"}>

                {showCursor && (
                    <motion.div
                        className="fake-cursor film-cursor"
                        animate={{
                            x: cursorPos.x,
                            y: cursorPos.y,
                            scale: clicking ? 0.85 : 1
                        }}
                        transition={{ type: 'spring', stiffness: 250, damping: 20 }}
                    >
                        {clicking && <span className="film-ripple" style={{ left: 2, top: 2 }} />}
                    </motion.div>
                )}

                <AnimatePresence mode="wait">
                    {!showDoc && step < 5 && (
                        <motion.form
                            key="form"
                            className="email-workflow-form"
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -40 }}
                            transition={{ duration: 0.65, type: 'spring' }}
                            onSubmit={e => e.preventDefault()}
                        >
                            <TextField
                                value={step === 1 ? typedEmail : field.email}
                                name="to"
                                placeholder="To: vienna@nougat.law"
                                className="email-field underline-email"
                            />
                            <TextField
                                value={step === 2 ? typedSubject : field.subject}
                                name="subject"
                                placeholder="Subject"
                                className="email-field subject"
                            />
                            <textarea
                                className="email-textarea"
                                value={step === 3 ? typedBody : field.body}
                                placeholder="Describe your legal issue"
                                name="body"
                            />
                            <motion.div
                                initial={false}
                                animate={isSending ? { y: 6, scale: 0.97, opacity: 0.7 } : { y: 0, scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', bounce: 0.3, duration: 0.6 }}
                            >
                                <Button
                                    className={
                                        'email-form-button button--primary-new button--full-width' + (isSending ? ' sending' : '')
                                    }
                                    type="button"
                                    disabled
                                >
                                    {isSending ? <span className="sending-cursor">Sending</span> : 'Send email'}
                                </Button>
                            </motion.div>
                        </motion.form>
                    )}

                    {!showDoc && sent && (
                        <motion.div
                            key="draftcard"
                            className="email-workflow-draft-card"
                            initial={{ opacity: 0, scale: 0.92, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.93, y: -30 }}
                            transition={{ duration: 0.7, type: 'spring' }}
                        >
                            <div className="draft-card-logo">
                                <img src={mailLogo} alt="logo" />
                            </div>
                            <div className="draft-card-title">{DOC_TITLE}</div>
                            <div
                                className={`draft-card-doc-link ${draftClicked ? 'disabled' : ''}`}
                                tabIndex={0}
                                role="button"
                                onClick={() => {
                                    if (!draftClicked) {
                                        setDraftClicked(true);
                                        setTimeout(() => setShowDoc(true), 400);
                                    }
                                }}
                                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setShowDoc(true)}
                            >
                                <span className="draft-card-attach-icon" />
                                <span className="draft-card-doc-fw">{DOC_FILE}</span>
                            </div>

                        </motion.div>
                    )}

                    {showDoc && (
                        <EmailWorkflowDocumentPreview />
                    )}

                </AnimatePresence>
            </div>
        </div>
    );
}

export default EmailWorkFlow;


